var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FormControlLabel, Grid, InputAdornment, Radio, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StatusPermission } from '../../..';
import { useUserPermissionsAPI } from '../../app/selectors';
import { FormField, NumberInput, RadioGroup } from '../../common';
import { useFormlessContext } from '../../hooks';
import { AttritionContent } from '../../wizard/attrition';
import { WizardContext } from '../../wizard/context';
import { OvertimeCostContent } from '../../wizard/financial/overtimeCost';
import { CurrencySelect } from '../../wizard/financial/staffSalaries';
import { Completed } from '../../wizard/queue/types';
import { useEffectiveQueueLocations } from '../../wizard/selectors';
import { ShrinkageContent, ShrinkageLocationSelector } from '../../wizard/shrinkage';
import { SupportStaffTable } from '../../wizard/supportStaff/locationRoleTable';
import { useQueueById } from '../selectors';
import { MultiskillType } from '../types';
import { MultiskillWarningDialog } from './multiskillWarningDialog';
export var MultiskillWizardContextProvider = function (_a) {
  var _b;
  var children = _a.children;
  var formless = useFormlessContext();
  var isDirty = (_b = useFormState({
    control: formless === null || formless === void 0 ? void 0 : formless.control,
    name: 'shrinkages'
  }).isDirty) !== null && _b !== void 0 ? _b : false;
  var handleSaveWithoutValidation = useCallback(function () {
    // In multiskill context, we don't need to save before changing location.
    // The form will be saved on submit.
  }, []);
  var value = useMemo(function () {
    return {
      methods: {
        formState: {
          isDirty: isDirty
        }
      },
      handleSaveWithoutValidation: handleSaveWithoutValidation,
      handleSave: function () {
        return function () {};
      },
      actionRef: {
        current: undefined
      },
      requesting: false,
      goToStep: function () {},
      goToQueue: function () {}
    };
  }, [isDirty, handleSaveWithoutValidation]);
  return _jsx(WizardContext.Provider, __assign({
    value: value
  }, {
    children: children
  }));
};
var MaxOccupancyThreshold = function (_a) {
  var isDisabledWAP = _a.isDisabledWAP,
    disabled = _a.disabled;
  var t = useTranslation(['wizard', 'multiSkill']).t;
  var control = useFormlessContext();
  var _b = useState(false),
    openOptimizedWarning = _b[0],
    setOpenOptimizedWarning = _b[1];
  var _c = useState(false),
    openWapWarning = _c[0],
    setOpenWapWarning = _c[1];
  return _jsxs(Grid, __assign({
    container: true,
    direction: "row",
    gap: 4,
    wrap: "nowrap"
  }, {
    children: [_jsx(Grid, __assign({
      item: true,
      xs: 4
    }, {
      children: _jsx(FormField, __assign({
        name: 'maxOccupancy',
        label: t('workVolume.maxOccupancy')
      }, {
        children: _jsx(NumberInput, {
          min: -1,
          max: 99.9999,
          decimal: 4,
          InputProps: {
            endAdornment: _jsx(InputAdornment, __assign({
              position: "end"
            }, {
              children: "%"
            }))
          },
          disabled: disabled
        })
      }))
    })), _jsx(Grid, __assign({
      item: true,
      xs: 4
    }, {
      children: _jsx(FormField, __assign({
        label: t('multiSkill:edit.chooseMultiSkillingMix'),
        name: 'multiskillType'
      }, {
        children: _jsxs(RadioGroup, __assign({
          row: true
        }, {
          children: [_jsx(FormControlLabel, {
            value: MultiskillType.All,
            control: _jsx(Radio, {}),
            label: t('multiSkill:edit.oneHundredPercent'),
            disabled: disabled
          }), _jsx(FormControlLabel, {
            value: MultiskillType.Optimized,
            control: _jsx(Radio, {}),
            label: t('multiSkill:edit.fullyOptimized'),
            disabled: disabled,
            onChange: function () {
              return setOpenOptimizedWarning(true);
            }
          })]
        }))
      }))
    })), _jsx(Grid, __assign({
      item: true,
      xs: 4
    }, {
      children: _jsx(FormField, __assign({
        label: t('multiSkill:edit.includeWorkArrivalPattern'),
        name: 'isUseWAP',
        disabled: isDisabledWAP
      }, {
        children: _jsxs(RadioGroup, __assign({
          row: true,
          "data-testid": "radioWapGroup"
        }, {
          children: [_jsx(FormControlLabel, {
            value: Completed.No,
            control: _jsx(Radio, {}),
            label: t('multiSkill:edit.no'),
            disabled: isDisabledWAP
          }), _jsx(FormControlLabel, {
            value: Completed.Yes,
            control: _jsx(Radio, {}),
            label: t('multiSkill:edit.yes'),
            disabled: isDisabledWAP,
            onChange: function () {
              return setOpenWapWarning(true);
            }
          })]
        }))
      }))
    })), _jsx(MultiskillWarningDialog, {
      openWarning: openOptimizedWarning,
      setOpenWarning: setOpenOptimizedWarning,
      message: t('multiSkill:edit.warningFullyOptimizedMessage'),
      onCancel: function () {
        return control === null || control === void 0 ? void 0 : control.setValue('multiskillType', MultiskillType.All);
      }
    }), _jsx(MultiskillWarningDialog, {
      openWarning: openWapWarning,
      setOpenWarning: setOpenWapWarning,
      message: t('multiSkill:edit.warningIncludeWapMessage'),
      onCancel: function () {
        return control === null || control === void 0 ? void 0 : control.setValue('isUseWAP', Completed.No);
      }
    })]
  }));
};
export var MultiSkillGroupDetail = React.memo(function (_a) {
  var _b, _c, _d;
  var activeData = _a.activeData,
    permission = _a.permission;
  var t = useTranslation(['wizard', 'multiSkill']).t;
  var firstChildId = (_b = activeData === null || activeData === void 0 ? void 0 : activeData.children) === null || _b === void 0 ? void 0 : _b[0];
  var queue = useQueueById(firstChildId);
  var queueLocations = useEffectiveQueueLocations();
  var financialPermission = useUserPermissionsAPI().financialPermission;
  var hideFinancial = financialPermission === StatusPermission.Hide;
  var hasQueueLocations = queueLocations.length > 0;
  var _e = useState((_d = (_c = queueLocations[0]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null),
    selectedQueueLocationId = _e[0],
    setSelectedQueueLocationId = _e[1];
  useEffect(function () {
    var _a, _b;
    setSelectedQueueLocationId((_b = (_a = queueLocations[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null);
  }, [queueLocations]);
  return _jsxs(Stack, __assign({
    spacing: 4
  }, {
    children: [_jsx(MaxOccupancyThreshold, {
      isDisabledWAP: !(queue === null || queue === void 0 ? void 0 : queue.enableUseWAP) || !permission,
      disabled: !permission
    }), _jsx(Typography, {
      children: t('multiSkill:edit.groupDescription')
    }), _jsx(Typography, __assign({
      mb: 4,
      variant: "subtitle2"
    }, {
      children: t('shrinkage.name')
    })), hasQueueLocations && _jsx(MultiskillWizardContextProvider, {
      children: _jsx(Box, __assign({
        sx: {
          width: 240
        }
      }, {
        children: _jsx(ShrinkageLocationSelector, {
          selectedQueueLocationId: selectedQueueLocationId,
          setSelectedQueueLocationId: function (id) {
            setSelectedQueueLocationId(id);
          }
        })
      }))
    }), _jsx(ShrinkageContent, {
      isReview: true,
      queueLocationId: selectedQueueLocationId
    }), _jsx(Typography, __assign({
      variant: "subtitle2"
    }, {
      children: t('attrition.fullName')
    })), _jsx(AttritionContent, {}), _jsxs(Stack, __assign({
      direction: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2
    }, {
      children: [_jsx(Typography, __assign({
        variant: "subtitle2"
      }, {
        children: t('supportStaff.supportStaffAndFinancials')
      })), !hasQueueLocations && _jsx(CurrencySelect, {})]
    })), _jsx(Box, {}), " ", _jsx(SupportStaffTable, {
      isReview: true,
      disableFinancial: hideFinancial
    }), _jsx(Typography, __assign({
      variant: "subtitle2"
    }, {
      children: t('financial.overtimeCosts')
    })), _jsx(OvertimeCostContent, {})]
  }));
});